import React from "react";
import { useInterval } from "@hex-insights/core";
import {
	MessageGroup,
	MessageOrderField,
	OrderDirection,
	useInitialViewRecord,
	useMessageGroupMessagesListQuery,
	useRecordView,
	UserViewRecordOrderField,
	useUserViewRecordListForTargetWithUserQuery,
} from "../../../Utilities";
import { getMessageGroupUserFilter } from "./filters";

export type UseMessageGroupMessagesArgs = {
	messageGroupID: MessageGroup["id"];
	pageSize?: number;
	poll?: boolean;
};

export function useMessageGroupMessages({ messageGroupID, pageSize = 25, poll = false }: UseMessageGroupMessagesArgs) {
	const { setInterval, clearInterval } = useInterval();
	const [recordView] = useRecordView(messageGroupID);
	React.useEffect(() => {
		recordView();
		return () => {
			recordView();
		};
	}, [recordView]);
	const { loading: loadingViewerViewRecord, initialViewRecord } = useInitialViewRecord(messageGroupID);
	const lastViewedAt = initialViewRecord?.lastViewedAt ?? null;

	const {
		data: userViewRecordData,
		startPolling: startPollingViewRecords,
		stopPolling: stopPollingViewRecords,
	} = useUserViewRecordListForTargetWithUserQuery({
		variables: {
			filters: {
				targetIDEQ: Number(messageGroupID),
				user: [
					{
						and: [{ isCurrentEQ: false }, getMessageGroupUserFilter(messageGroupID)],
					},
				],
			},
			order: { field: UserViewRecordOrderField.LastViewedAt, direction: OrderDirection.Desc },
		},
		fetchPolicy: "cache-and-network",
	});
	const otherUserViewRecords = React.useMemo(
		() => userViewRecordData?.userViewRecordConnection.edges.map((e) => e.node) ?? [],
		[userViewRecordData],
	);

	const { fetchMore, startPolling, stopPolling, ...queryResult } = useMessageGroupMessagesListQuery({
		variables: {
			filters: { messageGroupIDEQ: messageGroupID },
			pagination: { last: pageSize },
			order: { field: MessageOrderField.CreatedAt, direction: OrderDirection.Asc },
		},
		fetchPolicy: "cache-and-network",
		notifyOnNetworkStatusChange: true,
	});

	React.useEffect(() => {
		if (poll) {
			const intervalMS = 15000;
			startPolling(intervalMS);
			startPollingViewRecords(intervalMS);
			setInterval(() => recordView(), intervalMS);
		} else {
			stopPolling();
			stopPollingViewRecords();
			clearInterval();
		}
	}, [
		poll,
		startPolling,
		stopPolling,
		startPollingViewRecords,
		stopPollingViewRecords,
		setInterval,
		clearInterval,
		recordView,
	]);

	const loading = queryResult.loading;
	const hasMore = queryResult.data?.messageConnection.pageInfo.hasPreviousPage ?? true;
	const startCursor = queryResult.data?.messageConnection.pageInfo.startCursor;

	const loadMore = React.useCallback(() => {
		if (!hasMore || loading) {
			return;
		}
		fetchMore({
			variables: {
				pagination: { last: pageSize, before: startCursor },
			},
			updateQuery: (previousQueryResult, { fetchMoreResult }) => ({
				...previousQueryResult,
				messageConnection: {
					...previousQueryResult.messageConnection,
					pageInfo: fetchMoreResult.messageConnection.pageInfo,
					edges: [...fetchMoreResult.messageConnection.edges, ...previousQueryResult.messageConnection.edges],
				},
			}),
		});
	}, [hasMore, loading, fetchMore, pageSize, startCursor]);

	return {
		loadingViewerViewRecord,
		lastViewedAt,
		otherUserViewRecords,
		...queryResult,
		loadMore,
	};
}
